import { CheckCircleFilled, NotificationFilled } from '@ant-design/icons';
import { Button, Modal } from 'antd';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getProductId, isIOS } from '@/infrastructure/product-info';
import { stores } from '@/presentation/store';
import styles from './sdk-apple-review-announcement-modal.module.scss';

/** 若公告文案更新需再次全员展示，递增版本号 */
const STORAGE_KEY = 'monitor_client_bugly_sdk_apple_announcement_v1';

/** iOS SDK 接入文档 */
const SDK_APPLE_ANNOUNCEMENT_DOC_URL = 'https://bugly.tds.qq.com/docs/sdk/ios/';

export const SdkAppleReviewAnnouncementModal: FC = observer(() => {
  const location = useLocation();
  const { loaded: productStoreLoaded, productId: storeProductId, pid: storePid } = stores.ProductStore;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const hasProductContext = Boolean(getProductId());
    const eligible = productStoreLoaded && hasProductContext && isIOS();
    if (!eligible) {
      setVisible(false);
      return;
    }

    try {
      if (window.localStorage.getItem(STORAGE_KEY) === '1') {
        setVisible(false);
        return;
      }
    } catch {
      setVisible(true);
      return;
    }
    setVisible(true);
  }, [location.pathname, location.search, productStoreLoaded, storeProductId, storePid]);

  const handleAck = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* ignore quota / private mode */
    }
    setVisible(false);
  };

  const modalTitle = (
    <div className={styles.titleRow}>
      <div className={styles.titleIcon} aria-hidden>
        <NotificationFilled />
      </div>
      <div className={styles.titleText}>
        <div className={styles.titleMain}>平台公告</div>
        <span className={styles.titleSub}>
          请阅读以下内容，确认知悉后可关闭本提示
        </span>
      </div>
    </div>
  );

  return (
    <Modal
      wrapClassName={styles.wrap}
      title={modalTitle}
      visible={visible}
      closable={false}
      maskClosable={false}
      keyboard={false}
      width={680}
      centered
      footer={[
        <Button
          key="ack"
          type="primary"
          size="large"
          className={styles.ackBtn}
          onClick={handleAck}
        >
          我知道了
        </Button>,
      ]}
    >
      <div className={styles.body}>
        <div className={styles.leadBanner}>
          <p className={styles.leadTitle}>
            【重要通知】SDK 新版本发布 — 适配 Apple 最新审核策略
          </p>
        </div>

        <p className={styles.salutation}>尊敬的开发者：</p>

        <p className={styles.paragraph}>
          {
            '近期 Apple 对 App Store 的隐私审核策略进行了升级，对应用及第三方 SDK 中涉及设备信息相关的 API 调用和符号命名进行了更严格的静态扫描检测。受此影响，部分集成了Bugly旧版本 SDK 的应用在提审时可能会收到涉及"设备敏感信息采集"或"设备指纹"相关的审核驳回。'
          }
        </p>

        <p className={styles.paragraph}>
          {
            '在此郑重说明：Bugly从未采集任何设备敏感信息或设备指纹数据。 本次审核问题的根因是 SDK 内部分函数名及变量名中包含了与设备标识符相关的关键词（如 deviceId 等），被 Apple 的自动化扫描机制识别为疑似采集行为，从而触发了审核拦截。SDK 的实际功能逻辑和数据采集行为均未发生变化，不存在违规采集的情况。'
          }
        </p>

        <div className={styles.sectionLabel}>
          针对上述问题，我们已发布新版本 SDK，变更如下：
        </div>
        <ul className={styles.changeList}>
          <li className={styles.changeItem}>
            <CheckCircleFilled className={styles.itemIcon} aria-hidden />
            <strong>对涉及敏感关键词的函数名、变量名、类名等符号进行了全面重命名。</strong>
          </li>
        </ul>

        <p className={`${styles.paragraph} ${styles.paragraphAfterChangeList}`}>
          Bugly SDK 最新版本（iOS：2.8.2.6）已通过 Apple 最新审核策略验证。请各业务方尽快将 Bugly SDK 升级至最新版本（iOS：2.8.2.6），以避免后续提审受阻。
          <br />
          具体升级方式及版本号请参考
          <a
            className={styles.docLink}
            href={SDK_APPLE_ANNOUNCEMENT_DOC_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            接入文档
          </a>
          。如有任何疑问，请随时联系我们的技术支持团队。
        </p>
      </div>
    </Modal>
  );
});
